import type { MonopolyPayload } from './report';

/*
 * Two fallbacks for when the live report isn't available:
 *
 *   MONOPOLY_PREVIEW — the design handoff's SAMPLE figures (its own footer says
 *     "Figures shown are sample data for design review"). DEV ONLY, so the UI
 *     can be reviewed as the real thing before the pipeline exists.
 *   MONOPOLY_EMPTY   — the same structure with zeros. What a DEPLOYED build
 *     shows, so a client can never be served invented figures. Same discipline
 *     as PF_EMPTY / AH_EMPTY.
 *
 * Every figure below is transcribed from the design; if a number here changes,
 * the design changed. Per-day series are read off the design's chart geometry
 * (bar heights / SVG path points against their axes) and rounded.
 */

// 08 Sep → 19 Sep 2026 — the period the design shows (12 days).
const DAYS = Array.from({ length: 12 }, (_, i) => new Date(Date.UTC(2026, 8, 8 + i)).toISOString().slice(0, 10));

// Cards used per day — the hero line chart, 0–2.0M axis. Last value closes the
// sum on the hero total.
const CARDS_USED_BY_DAY = [983_000, 1_100_000, 1_317_000, 1_617_000, 1_433_000, 1_067_000, 950_000, 1_083_000, 1_200_000, 1_367_000, 1_717_000, 1_507_880];

// New participants per day — the bar labels on the Participants tab
// (268k … 69k); first is the launch peak, last is "▲ 69,204 today".
const NEW_PARTICIPANTS_BY_DAY = [268_140, 195_000, 152_000, 168_000, 141_000, 96_000, 88_000, 79_000, 74_000, 81_000, 92_000, 69_204];

// Consents collected per day — 12 days summing to the consent total above.
const CONSENTS_BY_DAY = [271_000, 214_000, 178_000, 165_000, 149_000, 122_000, 108_000, 118_000, 121_000, 133_000, 106_291, 78_000];

// Visits per day — the area chart, 0–500k axis.
const VISITS_BY_DAY = [411_000, 386_000, 400_000, 469_000, 431_000, 356_000, 339_000, 353_000, 369_000, 408_000, 453_000, 419_000];

// Cards used per day split — bar height as % of 2.0M, and the digital share of
// each bar, both from the Cards tab chart.
const CARDS_BAR_PCT = [49, 56, 66, 81, 72, 52, 47, 54, 60, 69, 86, 76];
const DIGITAL_SHARE_PCT = [18, 17, 16, 19, 21, 15, 14, 18, 19, 20, 22, 23];
const CARDS_BY_DAY = CARDS_BAR_PCT.map((p, i) => {
  const total = Math.round((p / 100) * 2_000_000);
  const digital = Math.round((DIGITAL_SHARE_PCT[i] / 100) * total);
  return { physical: total - digital, digital };
});

// Dice throws and board laps per day — grouped bars, 0–500k axis.
const DICE_PCT = [49, 55, 65, 82, 72, 52, 47, 54, 60, 70, 86, 77];
const LAPS_PCT = [7, 8, 10, 12, 11, 7, 6, 7, 9, 10, 13, 11];
const DICE_BY_DAY = DICE_PCT.map((p, i) => ({ rolled: Math.round((p / 100) * 500_000), laps: Math.round((LAPS_PCT[i] / 100) * 500_000) }));

export const MONOPOLY_PREVIEW: MonopolyPayload = {
  campaign: {
    name: 'Le Grand Jeu Monopoly — Carrefour France',
    subtitle: 'Tableau de bord temps réel · campagne de 5 semaines · 08 sept. – 12 oct. 2026',
    periodStart: '08/09/2026',
    periodEnd: '19/09/2026',
    currentDay: 12,
    totalDays: 35,
    live: true,
  },
  days: DAYS,
  overview: {
    cardsUsed: { total: 15_341_880, perDay: 1_278_490, printed: 70_000_000, physical: 12_704_318, digital: 2_637_562 },
    cardsUsedByDay: CARDS_USED_BY_DAY,
    participantsToday: 69_204,
    cardsPerParticipant: 10.2,
    cardsIssued: {
      physical: { used: 12_704_318, total: 62_000_000 },
      digital: { opened: 2_637_562, granted: 3_214_006 },
      codesRejectedPct: 1.8,
    },
    dice: {
      rolled: 4_208_455,
      throwsEarned: 5_113_960,
      boardsCompleted: 618_220,
      playersRolled: 1_142_308,
      avgRollsPerPlayer: 3.7,
      lapsPerPlayer: 0.54,
    },
  },
  participants: {
    unique: 1_503_412,
    optIns: 1_391_204,
    marketingConsents: 1_024_880,
    returningPct: 61.4,
    newByDay: NEW_PARTICIPANTS_BY_DAY,
    consentsByDay: CONSENTS_BY_DAY,
    funnel: [
      { label: "Écran d'accueil ouvert", value: 1_624_905, pct: 100 },
      { label: 'CGU acceptées', value: 1_391_204, pct: 85.6 },
      { label: 'Au moins un consentement donné', value: 1_024_880, pct: 63.1 },
      { label: 'Première carte utilisée', value: 1_238_660, pct: 76.2 },
    ],
    // Consents COLLECTED, the sum of the nine types below (a member who
    // re-consents counts each time), not the number of members behind them.
    consentsGranted: 1_763_291,

    consentChannels: [
      { label: 'Newsletter e-mail', value: 412_780, pct: 74 },
      { label: 'Newsletter téléphone', value: 228_904, pct: 41 },
      { label: 'WhatsApp', value: 318_145, pct: 57 },
      { label: 'Instagram', value: 267_530, pct: 48 },
      { label: 'Messenger', value: 183_970, pct: 33 },
      { label: 'KYC complété', value: 117_062, pct: 21 },
      { label: 'Être recontacté', value: 98_420, pct: 18 },
      { label: 'DV1', value: 74_310, pct: 13 },
      { label: 'DV2', value: 62_170, pct: 11 },
    ],
  },
  visits: {
    total: 4_780_412,
    uniqueVisitors: 1_503_412,
    sessionsPerVisitor: 3.18,
    avgSession: '4 min 12 s',
    byDay: VISITS_BY_DAY,
    sources: [
      { label: 'Bannière app Carrefour', pct: 44 },
      { label: 'Confirmation de commande', pct: 27 },
      { label: 'E-mail / CRM', pct: 17 },
      { label: 'QR en magasin / prospectus', pct: 12 },
    ],
    topScreens: [
      { label: 'Plateau de jeu / accueil', value: 4_102_880 },
      { label: 'Saisie du code carte', value: 3_455_190 },
      { label: 'Mes lots', value: 2_214_067 },
      { label: 'Collections de rues', value: 1_876_402 },
      { label: 'Règles & FAQ', value: 402_155 },
    ],
  },
  cards: {
    printed: 70_000_000,
    printedPhysical: 62_000_000,
    printedDigital: 8_000_000,
    digitalGranted: 3_214_006,
    avgPacksPerOrder: 2.4,
    codeErrors: 276_142,
    usersBlocked: 4_088,
    byDay: CARDS_BY_DAY,
    diceByDay: DICE_BY_DAY,
    streets: { claimed: 5_612_940, distinct: 27, total: 28, rare: 9_566 },
    channels: [
      { label: 'Drive', minOrder: 'min. 30 €', packs: 1_684_220, sharePct: 52.4, perOrder: 1.9 },
      { label: 'Livraison à domicile (LAD)', minOrder: 'min. 60 €', packs: 1_022_455, sharePct: 31.8, perOrder: 3.1 },
      { label: 'Livraison express (LEX)', minOrder: 'min. 20 €', packs: 507_331, sharePct: 15.8, perOrder: 1.2 },
    ],
  },
  prizes: {
    instantWins: 3_528_404,
    instantWinsTotal: 10_000_000,
    collectionsCompleted: 6_842,
    collectionGroups: 10,
    chanceCards: 884_120,
    chanceNote: '12 tirages quotidiens payés · 1 000 € chacun',
    collections: [
      { color: '#1C4FA1', name: 'Bleu foncé', prize: 'Séjour sur une île paradisiaque', stock: 2, won: 1, rare: 'RUE DE LA PAIX', rareClaimed: 0 },
      { color: '#1B8A3A', name: 'Vert', prize: 'Scooter électrique Easy-Watts', stock: 20, won: 6, rare: 'BOULEVARD DES CAPUCINES', rareClaimed: 9 },
      { color: '#E2001A', name: 'Rouge', prize: 'Casque à conduction osseuse', stock: 20, won: 4, rare: 'AVENUE HENRI-MARTIN', rareClaimed: 6 },
      { color: '#F2C500', name: 'Jaune', prize: 'Aspirateur Laveur', stock: 20, won: 5, rare: 'RUE LAFAYETTE', rareClaimed: 8 },
      { color: '#F07E13', name: 'Orange', prize: 'Smartbox Théâtre & Spectacles', stock: 400, won: 148, rare: 'PLACE PIGALLE', rareClaimed: 212 },
      { color: '#D93E8F', name: 'Rose', prize: 'Montre connectée', stock: 200, won: 64, rare: 'RUE DE PARADIS', rareClaimed: 96 },
      { color: '#74C7E8', name: 'Bleu clair', prize: 'Air Fryer', stock: 20, won: 7, rare: 'AVENUE DE LA REPUBLIQUE', rareClaimed: 11 },
      { color: '#8B5A2B', name: 'Marron', prize: 'Gourde Mug', stock: 20_000, won: 6_404, rare: 'RUE LECOURBE', rareClaimed: 9183 },
      { color: '#3D3D3D', name: 'Gares', prize: 'Pack permis complet', stock: 48, won: 15, rare: 'GARE MONTPARNASSE', rareClaimed: 24 },
      { color: '#00A9CE', name: 'Services publics', prize: '1 500 € offerts par Carrefour énergies', stock: 10, won: 3, rare: 'EAU', rareClaimed: 4 },
    ],
    instantWinMix: [
      { label: 'Coupons Carrefour (1 € / 0,50 €)', value: 1_984_110, pct: 56.2 },
      { label: 'Produits offerts', value: 1_102_660, pct: 31.3 },
      { label: 'Bons partenaires', value: 441_634, pct: 12.5 },
    ],
  },
};

const zeros = (n: number) => Array.from({ length: n }, () => 0);

export const MONOPOLY_EMPTY: MonopolyPayload = {
  campaign: { ...MONOPOLY_PREVIEW.campaign, currentDay: 0, live: false },
  days: DAYS,
  overview: {
    cardsUsed: { total: 0, perDay: 0, printed: MONOPOLY_PREVIEW.overview.cardsUsed.printed, physical: 0, digital: 0 },
    cardsUsedByDay: zeros(DAYS.length),
    participantsToday: 0,
    cardsPerParticipant: 0,
    cardsIssued: {
      physical: { used: 0, total: MONOPOLY_PREVIEW.overview.cardsIssued.physical.total },
      digital: { opened: 0, granted: 0 },
      codesRejectedPct: 0,
    },
    dice: { rolled: 0, throwsEarned: 0, boardsCompleted: 0, playersRolled: 0, avgRollsPerPlayer: 0, lapsPerPlayer: 0 },
  },
  participants: {
    unique: 0,
    optIns: 0,
    marketingConsents: 0,
    returningPct: 0,
    newByDay: zeros(DAYS.length),
    consentsByDay: zeros(DAYS.length),
    funnel: MONOPOLY_PREVIEW.participants.funnel.map((r) => ({ ...r, value: 0, pct: 0 })),
    consentChannels: MONOPOLY_PREVIEW.participants.consentChannels.map((r) => ({ ...r, value: 0, pct: 0 })),
  },
  visits: {
    total: 0,
    uniqueVisitors: 0,
    sessionsPerVisitor: 0,
    avgSession: '0 s',
    byDay: zeros(DAYS.length),
    sources: (MONOPOLY_PREVIEW.visits.sources ?? []).map((r) => ({ ...r, pct: 0 })),
    topScreens: (MONOPOLY_PREVIEW.visits.topScreens ?? []).map((r) => ({ ...r, value: 0 })),
  },
  cards: {
    ...MONOPOLY_PREVIEW.cards,
    digitalGranted: 0,
    avgPacksPerOrder: 0,
    codeErrors: 0,
    usersBlocked: 0,
    byDay: DAYS.map(() => ({ physical: 0, digital: 0 })),
    diceByDay: DAYS.map(() => ({ rolled: 0, laps: 0 })),
    channels: MONOPOLY_PREVIEW.cards.channels.map((c) => ({ ...c, packs: 0, sharePct: 0, perOrder: 0 })),
  },
  prizes: {
    ...MONOPOLY_PREVIEW.prizes,
    instantWins: 0,
    collectionsCompleted: 0,
    chanceCards: 0,
    collections: MONOPOLY_PREVIEW.prizes.collections.map((c) => ({ ...c, won: 0 })),
    instantWinMix: MONOPOLY_PREVIEW.prizes.instantWinMix.map((r) => ({ ...r, value: 0, pct: 0 })),
  },
};
